import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  postUserVisitsData,
  exportUserVisitsData,
} from 'redux/userVisitsArchive/userVisitsArchive.actions';
import FilterBar from './components/FilterBar';
import Pagination from './components/Pagination';
import Table from './components/Table';
import styles from './styles.module.scss';
import AccessOverlay from 'components/AccessOverlay';
import useTranslation from 'hooks/useTranslation';
import DashboardSectionHeader from 'layouts/Dashboard/DashboardSectionHeader';
import DashboardMainSectionWrapper from 'layouts/Dashboard/DashboardMainSectionWrapper';
import { SelectBox } from 'components';
import ExportButton from 'components/ExportButton';
import { RAYCHAT_WEBSITE_URL } from 'utils/constants';
import { ApiLoader } from 'components';
import moment from 'jalali-moment';
import { getCalenderLastDays } from 'utils/date';

const lastWeekDate = getCalenderLastDays(6, 'days');

const INITIAL_STATE = {
  pageSize: 10,
  currentPage: 1,
  date: lastWeekDate,
  email: '',
  phone: '',
  hadConversation: false,
};

const USER_VISITS_COUNT = [
  {
    title: '5',
    value: 5,
  },
  {
    title: '10',
    value: 10,
  },
  {
    title: '25',
    value: 25,
  },
];

const mapState = ({ appData, userVisitsData, websitesData }) => ({
  locale: appData.locale,
  userVisitsData: userVisitsData.data,
  isFetching: userVisitsData.isFetching,
  selectedWorkspace: websitesData.selectedWorkspace?.id,
});

const ALLOWED_TYPES = [3, 7, 8, 10, 11]; // آزمایشی + تجاری + پریمیوم + پریمیوم پلاس + وی آی پی

const UserVisitsArchive = () => {
  const [transformedData, setTransformedData] = useState([]);
  const [filterOptions, setFilterOptions] = useState(INITIAL_STATE);

  const handleFilterFieldChange = (key, value) => {
    setFilterOptions({ ...filterOptions, [key]: value });
  };

  const { getTranslation } = useTranslation('ARCHIVE');
  const { t } = getTranslation();

  const dispatch = useDispatch();
  const { isFetching, userVisitsData, selectedWorkspace } =
    useSelector(mapState);
  const isTableHasData = userVisitsData?.usersCount > 0;

  const TABLE_HEAD_ITEMS = [
    {
      id: 'name',
      content: t('USER_VISITS_TABLE_HEAD_1'),
    },
    {
      id: 'createdAt',
      content: t('USER_VISITS_TABLE_HEAD_2'),
    },
    {
      id: 'conversation',
      content: t('USER_VISITS_TABLE_HEAD_3'),
    },
    {
      id: 'online',
      content: t('USER_VISITS_TABLE_HEAD_4'),
    },
    {
      id: 'email',
      content: t('USER_VISITS_TABLE_HEAD_5'),
    },
    {
      id: 'phone',
      content: t('USER_VISITS_TABLE_HEAD_6'),
    },
  ];

  const handleChangePageSize = (value) => {
    handleFilterFieldChange('pageSize', value);

    if (selectedWorkspace) {
      const firstDate = moment(new Date(filterOptions.date[0])).format(
        `YYYY-MM-DD`
      );
      const secondDate = filterOptions.date[1]
        ? moment(new Date(filterOptions.date[1])).format(`YYYY-MM-DD`)
        : firstDate;

      const data = {
        channelId: selectedWorkspace,
        skip: filterOptions.currentPage,
        limit: value,
        filter: {
          startDate: new Date(`${firstDate}T00:00:00`).toISOString(),
          endDate: new Date(`${secondDate}T00:00:00`).toISOString(),
          email: filterOptions.email,
          phone: filterOptions.phone,
          hadConversation: filterOptions.hadConversation ? 1 : 0,
        },
      };

      dispatch(postUserVisitsData(data));
    }
  };

  const handlePaginationChange = (pageNum) => {
    handleFilterFieldChange('currentPage', pageNum);

    if (selectedWorkspace) {
      const firstDate = moment(new Date(filterOptions.date[0])).format(
        `YYYY-MM-DD`
      );
      const secondDate = filterOptions.date[1]
        ? moment(new Date(filterOptions.date[1])).format(`YYYY-MM-DD`)
        : firstDate;

      const data = {
        channelId: selectedWorkspace,
        skip: pageNum,
        limit: filterOptions.pageSize,
        filter: {
          startDate: new Date(`${firstDate}T00:00:00`).toISOString(),
          endDate: new Date(`${secondDate}T00:00:00`).toISOString(),
          email: filterOptions.email,
          phone: filterOptions.phone,
          hadConversation: filterOptions.hadConversation ? 1 : 0,
        },
      };

      dispatch(postUserVisitsData(data));
    }
  };

  const exportBtnHandler = () => {
    const firstDate = moment(new Date(filterOptions.date[0])).format(
      `YYYY-MM-DD`
    );
    const secondDate = filterOptions.date[1]
      ? moment(new Date(filterOptions.date[1])).format(`YYYY-MM-DD`)
      : firstDate;

    const data = {
      channelId: selectedWorkspace,
      filter: {
        startDate: new Date(`${firstDate}T00:00:00`).toISOString(),
        endDate: new Date(`${secondDate}T00:00:00`).toISOString(),
        email: filterOptions.email,
        phone: filterOptions.phone,
        hadConversation: filterOptions.hadConversation ? 1 : 0,
      },
    };

    dispatch(exportUserVisitsData(data));
  };

  useEffect(() => {
    if (userVisitsData?.users) {
      const newData = userVisitsData?.users.map((item) => ({
        id: item.id,
        conversationId: item?.conversationId,
        value: [
          { id: 'name', content: item.name },
          {
            id: 'createdAt',
            content: moment
              .utc(item.createdAt)
              .local()
              .format('jYYYY/jMM/jDD - HH:mm'),
          },
          {
            id: 'conversation',
            content: item?.conversationId
              ? t('USER_VISITS_CONV_TABLE_ITEM_YES')
              : t('USER_VISITS_CONV_TABLE_ITEM_NO'),
          },
          {
            id: 'online',
            content: item.online
              ? t('USER_VISIT_STATUS_TABLE_ITEM_ONLINE')
              : t('USER_VISIT_STATUS_TABLE_ITEM_OFFLINE'),
          },
          { id: 'email', content: item.email },
          { id: 'phone', content: item.phone || '-' },
        ],
      }));

      setTransformedData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userVisitsData]);

  const renderUserVisitsResult = () => {
    if (userVisitsData?.usersCount === 0)
      return <h2>{t('NO_CONV_FOUND_MSG')}</h2>;
    return (
      <div className={styles.table_wrapper}>
        <>
          {isFetching && (
            <div className={`${styles.loader_layer} `}>
              <ApiLoader className={styles.loader} />
            </div>
          )}
          <Table tHeadItems={TABLE_HEAD_ITEMS} tbodyItems={transformedData} />
        </>
      </div>
    );
  };

  const renderPurchaseLink = (
    <a
      target='_blank'
      rel='noreferrer'
      href={`${RAYCHAT_WEBSITE_URL}/dashboard/payment/package-upgrading`}
    >
      {t('BTN_DISABLED_PURCHASE_LINK_TITLE')}
    </a>
  );

  const getTooltipContent = () => {
    return (
      <>
        <span>{t('BTN_DISABLED_TOOLTIP')}</span> {renderPurchaseLink}
      </>
    );
  };

  return (
    <>
      <AccessOverlay allowedTypes={ALLOWED_TYPES} />

      <DashboardSectionHeader>
        <div className={styles.user_visits_archive}>
          <h2 className={styles.user_visits_archive_title}>
            {t('USER_VISITS_FILTER_TITLE')}
          </h2>

          <FilterBar
            onChangeFilterField={handleFilterFieldChange}
            date={filterOptions.date}
            email={filterOptions.email}
            pageSize={filterOptions.pageSize}
            phone={filterOptions.phone}
            hadConversation={filterOptions.hadConversation}
          />

          <div className={styles.user_visits_archive_exports}>
            <div className={styles.user_visits_list__header__titles}>
              <h2 className={styles.user_visits_list__header__title}>
                {t('USER_VISITS_LIST_TITLE')}
              </h2>
              <h2 className={styles.total_user_visits_count}>
                {userVisitsData?.usersCount || 0}{' '}
                {t('USER_VISITS_COUNT_NUM_LABEL')}
              </h2>
            </div>

            {isTableHasData && selectedWorkspace && (
              <div className={styles.export_btn_container}>
                <ExportButton
                  className={styles.excel_export_btn}
                  text={t('EXCEL_EXPORT_LABEL')}
                  tooltipContent={getTooltipContent()}
                  onClick={exportBtnHandler}
                />
                <SelectBox
                  onChange={handleChangePageSize}
                  value={filterOptions.pageSize}
                  options={USER_VISITS_COUNT}
                  className={styles.user_visits_count_select}
                />
              </div>
            )}
          </div>
        </div>
      </DashboardSectionHeader>

      {isFetching ? (
        <ApiLoader />
      ) : (
        <DashboardMainSectionWrapper>
          {isTableHasData ? (
            <div>
              {renderUserVisitsResult()}
              <Pagination
                currentPage={filterOptions.currentPage}
                pageSize={filterOptions.pageSize}
                totalCount={userVisitsData?.usersCount}
                onPageChange={handlePaginationChange}
              />
            </div>
          ) : (
            <h2 className={styles.user_visits_list_empty}>
              {t('USER_VISITS_LIST_EMPTY')}
            </h2>
          )}
        </DashboardMainSectionWrapper>
      )}
    </>
  );
};

export default UserVisitsArchive;
